import { z } from "zod";

/**
 * URL state for the upstream-to-downstream page and its embed.
 *
 * Every control lives in the query string so a view can be bookmarked, shared
 * or dropped into an iframe. Keep this file free of React so it stays cheap to
 * test.
 */

/* The comparison period is stored in the database as a pseudo water year
   alongside the real ones, which is why these are numbers and not labels. */
export const COMPARISON_PERIODS = [
  { value: 5555, label: "Last 5 water years" },
  { value: 1111, label: "Last 10 water years" },
  { value: 9999, label: "All prior water years" },
];

export const COMPARISON_VALUES = COMPARISON_PERIODS.map((p) => p.value);

export const FLOW_CONDITIONS = [
  { value: "alldata", label: "All flow" },
  { value: "baseflow", label: "Baseflow" },
  { value: "stormflow", label: "Stormflow" },
];

export const VIEWS = [
  { value: "boxplot", label: "Box plots" },
  { value: "line", label: "Line chart" },
];

export const STATISTICS = [
  { value: "median", label: "Median" },
  { value: "mean", label: "Mean" },
];

export const SCALES = [
  { value: "linear", label: "Linear" },
  { value: "log", label: "Log" },
];

/* Only concentrations get a log axis. Nutrients and solids span orders of
   magnitude between baseflow and storms, and across reaches; temperature,
   pH, saturation and redox potential do not, and a log axis on them would
   mislead. */
export const CONCENTRATION_UNITS = ["ug/L", "mg/L", "uS/cm"];
export const isConcentration = (unitName) =>
  CONCENTRATION_UNITS.includes(unitName);

/* Below this many values a box is drawn over its actual samples as well,
   because a box summarising two or three points implies a distribution that
   is not there. Every synoptic site's single year and McMurdo's six per year
   fall under it; the monthly sites' single year (13 to 17) do not. */
export const SMALL_SAMPLE_LIMIT = 10;

/* The three reaches that still have sampling at more than one site. Shop Creek
   is left out because it has no results after WY2014, and Piney Creek because
   PC-1 is the only site on it, so there is no gradient to read. Both stay
   selectable. McMurdo Gulch draws on two sites, MCM-1 and MCM-2; the other
   three were sampled once, in 2013. */
export const DEFAULT_REACHES = [105, 106, 109];

export const DEFAULTS = {
  parameterid: 28, // Total Phosphorus
  year: null, // resolved to the latest water year with data
  comparison: 5555,
  flow: "alldata",
  reaches: DEFAULT_REACHES,
  view: "boxplot",
  statistic: "median",
  parameter2: null,
  scale: "linear",
  /* Off by default: the reaches differ by an order of magnitude, so one axis
     for all of them flattens the smaller ones. The lock is there for when the
     question is "which reach is higher" rather than "what happens along it". */
  lockYAxis: false,
};

/** Water year n runs 1 October n-1 through 30 September n. */
export const waterYearOf = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return d.getMonth() + 1 >= 10 ? d.getFullYear() + 1 : d.getFullYear();
};

export const isComparisonPeriod = (year) => COMPARISON_VALUES.includes(year);

export const comparisonLabel = (value) =>
  COMPARISON_PERIODS.find((p) => p.value === value)?.label ?? null;

/**
 * Labels a comparison box from the range actually present in the response
 * rather than from a fixed string, because the window is anchored to the
 * latest water year with data and not to today.
 */
export const periodLabel = (collectYear, waterYearMin, waterYearMax) => {
  if (!isComparisonPeriod(collectYear)) return `WY ${collectYear}`;
  if (waterYearMin == null || waterYearMax == null)
    return comparisonLabel(collectYear) ?? "Comparison";
  if (waterYearMin === waterYearMax) return `WY ${waterYearMin}`;
  return `WY ${waterYearMin} to ${waterYearMax}`;
};

/* A comma separated list of integers, empty meaning "no filter". */
const numberList = (fallback) =>
  z
    .preprocess((v) => {
      if (v == null || v === "") return fallback;
      const raw = Array.isArray(v) ? v : String(v).split(",");
      const ids = raw
        /* Number("") is 0, not NaN, so blanks have to go before converting. */
        .map((n) => String(n).trim())
        .filter((n) => n !== "")
        .map(Number)
        .filter((n) => Number.isInteger(n));
      return ids.length ? ids : fallback;
    }, z.array(z.number().int()))
    .default(fallback);

const nullableInt = z
  .preprocess(
    (v) => (v == null || v === "" ? null : Number(v)),
    z.number().int().nullable()
  )
  .default(null);

const boolean = (fallback) =>
  z
    .preprocess((v) => {
      if (v == null || v === "") return fallback;
      return v === true || v === "true";
    }, z.boolean())
    .default(fallback);

export const usdsSchema = z.object({
  parameterid: z.coerce.number().int().default(DEFAULTS.parameterid),
  year: nullableInt,
  comparison: z.coerce
    .number()
    .int()
    .refine((v) => COMPARISON_VALUES.includes(v), {
      message: "unknown comparison period",
    })
    .default(DEFAULTS.comparison),
  flow: z.enum(FLOW_CONDITIONS.map((f) => f.value)).default(DEFAULTS.flow),
  reaches: numberList(DEFAULTS.reaches),
  view: z.enum(VIEWS.map((v) => v.value)).default(DEFAULTS.view),
  statistic: z.enum(STATISTICS.map((s) => s.value)).default(DEFAULTS.statistic),
  parameter2: nullableInt,
  scale: z.enum(SCALES.map((s) => s.value)).default(DEFAULTS.scale),
  lockYAxis: boolean(DEFAULTS.lockYAxis),
});

/* The embed adds the two knobs every other embed on this site understands. */
export const usdsEmbedSchema = usdsSchema.extend({
  controls: boolean(false),
  height: z
    .preprocess(
      (v) => (typeof v === "string" ? parseInt(v, 10) : v),
      z.number()
    )
    .default(420),
});

/**
 * Turns parsed params into the query the API expects.
 * @param {object} params parsed URL state
 * @param {number} resolvedYear the water year to draw, after defaults
 * @returns {object} query parameters for /api/usds
 */
export const toApiQuery = (params, resolvedYear) => {
  const query = {
    parameterid: params.parameterid,
    year: resolvedYear,
    comparison: params.comparison,
    flow: params.flow,
  };
  if (params.reaches?.length) query.reaches = params.reaches.join(",");
  return query;
};

/**
 * A select may only be handed a value its option list contains, otherwise MUI
 * warns and shows nothing. Lists fetched from the API start out empty, so a
 * default from the URL has to wait until the options that match it arrive.
 * @param {string|string[]} value the wanted value, or values for a multi select
 * @param {array} options [{value}] currently available
 * @returns {string|string[]} the value if present, else "" (or the present subset)
 */
export const inOptions = (value, options) => {
  const present = new Set((options ?? []).map((o) => String(o.value)));
  if (Array.isArray(value)) return value.filter((v) => present.has(String(v)));
  return value != null && present.has(String(value)) ? String(value) : "";
};
